<template>
	<div :style="{ display: 'flex', justifyContent: 'end', margin: '20px' }">
		<LanguageDropdown :languageList="languageList" :defaultLang="language"
			@language-changed="handleLanguageChange" />
	</div>

	<div class="styleDiv">
		<div v-if="loading" class="loader-container">
			<div class="simple-spinner"></div>
		</div>
		<div v-else>
			<a-form class="form">
				<div>
					<img src="../assets/logo.png" alt="Vue logo" class="image" />
					<div :style="{ display: 'flex', justifyContent: 'center ' }">
						<div class="deleteImage">
							<DeleteFilled style="font-size: 2.5em; color: #522537" />
						</div>
					</div>
					<div
						style="text-align: center; margin: 10px 0 5px 0; font-size: 20px; color: #522537; font-weight: 600; letter-spacing: 0.5px; font-family: sans-serif">
						{{ t('del_ui_del_page_heading') }}</div>
					<div
						style="text-align: center; margin: 10px 0 15px 0; font-size: 15px; color: gray; height: 80px; display: flex; align-items: center;">
						{{ t('del_ui_del_page_intro_1') }}
					</div>
					<div style="text-align: center; margin: 10px 0 25px 0; font-size: 15px; color: gray;">
						<b>{{ t('del_ui_del_page_intro_2') }}</b>
					</div>

					<div style="text-align: center; margin: 10px 0 20px 0; font-size: 15px; color: gray">{{
						t('del_ui_del_page_intro_3') }}</div>
				</div>

				<div class="flexDisplay mrgb">
					<div class="ant-form-text" type="secondary">{{ identifier }}</div>
				</div>

				<div class="center">
					<a-button size="large" class="btn" @click="handleCancel">{{ t('del_ui_del_btn_cancel') }}</a-button>
					<a-button type="primary" size="large" class="btn" @click="handleDelete">{{
						t('del_ui_del_btn_delete_account') }}</a-button>
				</div>
			</a-form>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { DeleteFilled, ExclamationCircleOutlined } from "@ant-design/icons-vue";
import { Modal } from "ant-design-vue";
import { createVNode, onMounted, ref } from "vue";
import { deletedUserApi } from "@/services/api/auth";
import { openNotificationWithIcon } from "@/utils";
import router from "@/router";
import LanguageDropdown from "@/components/LanguageDropdown.vue";

// local state
const identifier = ref("");
const userId = ref("");
const loading = ref(false);

// translations state
const language = ref(localStorage.getItem("lang_for_delete") || "EN");
const translate = ref<any[]>([]);
const languageList = ref<any[]>([]);

const initTranslations = (stored: any) => {
	translate.value = stored?.deletePageTranslations || [];
	languageList.value = stored?.languageList || [];
};

const t = (key: string, params: Record<string, string> = {}): string => {
	const item = translate.value.find(i => i.key === key);
	if (!item) return key;
	const selectedLang = languageList.value.find(l => l.key === language.value);
	const langId = selectedLang?._id;
	let value = item.translate.find((t: any) => t.languageId === langId && t.isPublished)?.value;
	if (!value) {
		const enLang = languageList.value.find(l => l.key.toLowerCase() === "en");
		const enId = enLang?._id;
		value = item.translate.find((t: any) => t.languageId === enId && t.isPublished)?.value;
	}
	if (!value) value = item.base;
	return value;
};

const setLanguage = (lang: string) => {
	language.value = lang;
	localStorage.setItem("lang_for_delete", lang);
};

const handleLanguageChange = (lang: string) => {
	setLanguage(lang);
};

// data fetch
const fetchData = async () => {
	loading.value = true;
	const userData = localStorage.getItem("user");
	const languages = localStorage.getItem("delete_page_translate");

	if (languages) {
		const parsedLanguage = JSON.parse(languages || "{}");
		initTranslations(parsedLanguage); // <-- hydrate
	}

	if (!userData) {
		router.push("/login");
		loading.value = false;
		return;
	}

	if (userData && typeof userData === "string") {
		const user = JSON.parse(userData);
		identifier.value = user.identifier || "";
		userId.value = user._id || "";
	}

	loading.value = false;
};
onMounted(fetchData);

// delete handling
const handleDelete = () => {
	Modal.confirm({
		centered: true,
		width: 500,
		title: t("del_ui_del_model_info"),
		icon: createVNode(ExclamationCircleOutlined),
		okText: t("del_ui_del_model_del_btn"),
		okType: "danger",
		cancelText: t("del_ui_del_btn_cancel"),
		async onOk() {
			try {
				const userData = localStorage.getItem("user");
				if (userData) {
					const user = JSON.parse(userData);
					const deleteUser = await deletedUserApi(user._id, "deleteUser");
					if (deleteUser) {
						localStorage.removeItem("user");
						return openNotificationWithIcon("success", t("del_ui_account_delete_msg"));
					}
				}
			} catch (error: any) {
				if (!error.response && error.message === "Network Error") {
					return openNotificationWithIcon("error", t("del_ui_network_error"));
				}
				if (error?.response?.status === 404) {
					return openNotificationWithIcon("error", t("del_ui_user_not_exist"));
				} else {
					return openNotificationWithIcon("error", t("del_ui_support_team_msg"));
				}
			} finally {
				router.push("/login");
			}
		},
	});
};

const handleCancel = () => {
	router.push("/login");
};
</script>

<style scoped>
.styleDiv {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.form {
	background-color: #fff;
	width: 430px;
	padding: 3rem;
	box-shadow: 0 10px 25px rgba(92, 99, 105, 0.50);
	border-radius: 15px;

	.ant-form-item-label {
		position: relative;
		height: 48px;
	}
}

.image {
	width: 55%;
	height: auto;
	margin: auto;
	display: block;
}

.flexDisplay {
	display: flex;
	font-size: 16px;
	justify-content: center;
}

.mrgb {
	margin-bottom: 20px;
}

.ant-form-text {
	font-weight: 600;
	font-size: 18px;
	font-family: sans-serif;
	letter-spacing: 0.3px;
}

.center {
	display: flex;
	justify-content: center;
	gap: 10px;
}

.deleteImage {
	border: 2px solid #522537;
	border-radius: 60px;
	width: 90px;
	height: 90px;
	display: flex;
	justify-content: center;
	margin-top: 30px;
}

.btn {
	width: 80%;
	margin-top: 10px;
}

.loader-container {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 200px;
}

.simple-spinner {
	width: 40px;
	height: 40px;
	border: 4px solid #ccc;
	border-top-color: #44142a;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
